import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchClientDropoffReport } from "../../../../store/thunks/reportsThunk";
import ClientModal from "../../../../modals/ClientModal";
import { fetchClientById } from "../../../../store/thunks/clientsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

const ClientDropoffReport = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 10;
  const {
    data: clients,
    loading,
    error,
  } = useSelector((state) => state.reports.clientDropoff || { data: null, loading: false, error: null });
  
  useEffect(() => {
    dispatch(fetchClientDropoffReport());
  }, [dispatch]);

  const columns = [
    {
      header: "Client Name",
      accessor: "name",
      sortable: true,
    },
    {
      header: "Email",
      accessor: "email",
      sortable: true,
    },
    {
      header: "Additional Emails",
      accessor: "additionalEmails",
      sortable: true,
    },
    {
      header: "Last Appointment",
      accessor: (row) =>
        row.lastAppointment
          ? format(new Date(row.lastAppointment), "MM/dd/yyyy")
          : "Never",
      sortable: true,
    },
    {
      header: "Days Since Last Visit",
      accessor: "daysSinceLastVisit",
      cell: (row) => row.daysSinceLastVisit || "N/A",
      sortable: true,
    },
    {
      header: "Future Appointments",
      accessor: (row) => (row.hasFutureAppointment ? "Yes" : "No"),
      sortable: false,
    },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (clientId) => {
    dispatch(fetchClientById(clientId));
    setIsModalOpen(true);
  };

  const handleDownloadExcel = () => {
    if (!clients || clients.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const data = clients.map((client) => ({
      "Client Name": client.name || "N/A",
      Email: client.email || "N/A",
      "Last Appointment": client.lastAppointment
        ? format(new Date(client.lastAppointment), "MM/dd/yyyy")
        : "Never",
      "Days Since Last Visit": client.daysSinceLastVisit || "N/A",
      "Future Appointments": client.hasFutureAppointment ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Client Dropoff");

    const fileName = `Client_Dropoff_Report_${format(
      new Date(),
      "yyyy-MM-dd"
    )}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  // Calculate pagination
  const totalItems = Array.isArray(clients) ? clients.length : 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Sort data
  let sortedData = [...(clients || [])];
  if (sortConfig.key && sortConfig.direction) {
    sortedData.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "lastAppointment") {
        aValue = a.lastAppointment ? new Date(a.lastAppointment) : new Date(0);
        bValue = b.lastAppointment ? new Date(b.lastAppointment) : new Date(0);
      }

      if (sortConfig.key === "daysSinceLastVisit") {
        aValue = a.daysSinceLastVisit || Number.MAX_SAFE_INTEGER;
        bValue = b.daysSinceLastVisit || Number.MAX_SAFE_INTEGER;
      }

      if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }

  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-sm rounded-sm">
        <Table
          columns={columns}
          data={paginatedData}
          title="Client Dropoff Report"
          description="View clients and their appointment history"
          showPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalItems}
          onPageChange={handlePageChange}
          sortConfig={sortConfig}
          onSortChange={handleSort}
          onRowClick={handleRowClick}
          actions={[
            {
              label: "Download as Excel",
              onClick: handleDownloadExcel,
            },
          ]}
        />
      </div>
      <ClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default ClientDropoffReport;